import React from 'react';
import { Alert, Button, CircularProgress, TableBody, TableCell, TableRow, ToggleButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { RewardThresholdType } from 'api/models/request/IUpdateRewardThresholdsRequest';
import { useAuthContext } from 'hooks/useAuthContext';
import { useGetWorkspaceRewardThresholds } from 'hooks/rewardThresholds/useGetWorkspaceRewardThresholds';
import { useUpdateWorkspaceRewardThresholds } from 'hooks/rewardThresholds/useUpdateWorkspaceRewardThresholds';
import { REWARD_LEVELS, ThresholdField, useRewardThresholdsForm } from 'hooks/rewardThresholds/useRewardThresholdsForm';
import {
    StyledActions,
    StyledAdornmentUnitLabel,
    StyledCellText,
    StyledHeader,
    StyledHeaderCell,
    StyledLevelCell,
    StyledOrderAlert,
    StyledSection,
    StyledSubmitButton,
    StyledTable,
    StyledTableContainer,
    StyledTableHead,
    StyledThresholdTextField,
    StyledTightCellText,
    StyledUnitAdornment,
    StyledUnitLabel,
    StyledUnitToggleGroup,
} from './RewardThresholdsSettings.styles';

const wasAlreadyNotified = (error: unknown): boolean =>
    Boolean(error && typeof error === 'object' && 'notified' in error && (error as { notified?: boolean }).notified);

export const RewardThresholdsSettings = () => {
    const { userState } = useAuthContext();
    const workspaceId = userState.user.selectedWorkspace.workspaceId;
    return workspaceId ? <WorkspaceRewardThresholds key={workspaceId} workspaceId={workspaceId} /> : null;
};

const WorkspaceRewardThresholds = ({ workspaceId }: { workspaceId: string }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { rewardThresholds, rewardThresholdsLoading, rewardThresholdsError, refetchRewardThresholds } = useGetWorkspaceRewardThresholds(workspaceId);
    const { updateRewardThresholds, updatingRewardThresholds } = useUpdateWorkspaceRewardThresholds(workspaceId);
    const {
        values, percentage, maximum, isInvalidValue,
        validNumbers, validGoldOrder, validSilverOrder, valid,
        setThresholdType, setFieldValue, resetDraft, toRequest,
    } = useRewardThresholdsForm(rewardThresholds);

    const unitLabel = percentage ? '%' : t('rewardThresholds.points');

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const request = toRequest();
        if (!valid || !request || updatingRewardThresholds) return;

        updateRewardThresholds(request, {
            onSuccess: () => {
                resetDraft();
                enqueueSnackbar(t('rewardThresholds.saveSuccess'), { variant: 'success' });
            },
            onError: (error: unknown) => {
                if (!wasAlreadyNotified(error)) {
                    enqueueSnackbar(t('rewardThresholds.saveError'), { variant: 'error' });
                }
            },
        });
    };

    return (
        <StyledSection aria-labelledby="reward-thresholds-title">
            <StyledHeader>
                <div>
                    <Typography id="reward-thresholds-title" variant="h6">{t('rewardThresholds.title')}</Typography>
                    <Typography color="text.secondary">{t('rewardThresholds.description')}</Typography>
                </div>
                <StyledUnitToggleGroup
                    exclusive
                    size="small"
                    value={values?.thresholdType ?? RewardThresholdType.Percentage}
                    disabled={!values || updatingRewardThresholds}
                    aria-label={t('rewardThresholds.unit')}
                    onChange={(_, thresholdType: RewardThresholdType | null) => {
                        if (thresholdType !== null) setThresholdType(thresholdType);
                    }}
                >
                    <ToggleButton value={RewardThresholdType.Percentage}>
                        <StyledUnitLabel>%</StyledUnitLabel>
                    </ToggleButton>
                    <ToggleButton value={RewardThresholdType.Points}>
                        <StyledUnitLabel>{t('rewardThresholds.points')}</StyledUnitLabel>
                    </ToggleButton>
                </StyledUnitToggleGroup>
            </StyledHeader>

            {rewardThresholdsLoading && <CircularProgress size={24} aria-label={t('rewardThresholds.loading')} />}
            {rewardThresholdsError && (
                <Alert severity="error" action={<Button onClick={() => refetchRewardThresholds()}>{t('rewardThresholds.retry')}</Button>}>
                    {t('rewardThresholds.loadError')}
                </Alert>
            )}

            {values && (
                <form onSubmit={handleSubmit}>
                    <StyledTableContainer>
                        <StyledTable size="small">
                            <StyledTableHead>
                                <TableRow>
                                    <StyledHeaderCell>
                                        <StyledCellText>{t('rewardThresholds.reward')}</StyledCellText>
                                    </StyledHeaderCell>
                                    <StyledHeaderCell>
                                        <StyledCellText>{t('rewardThresholds.minimumScore')}</StyledCellText>
                                    </StyledHeaderCell>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {REWARD_LEVELS.map(level => {
                                    const field = `${level}ThresholdValue` as ThresholdField;
                                    const label = t(`rewardThresholds.${level}`);
                                    const invalid = isInvalidValue(values[field]);
                                    return (
                                        <TableRow key={level}>
                                            <StyledLevelCell component="th" scope="row">
                                                <StyledTightCellText>{label}</StyledTightCellText>
                                            </StyledLevelCell>
                                            <TableCell>
                                                <StyledThresholdTextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    value={values[field]}
                                                    disabled={updatingRewardThresholds}
                                                    error={invalid}
                                                    helperText={invalid ? t(percentage ? 'rewardThresholds.invalidValue' : 'rewardThresholds.invalidPoints', { max: maximum }) : undefined}
                                                    onChange={event => setFieldValue(field, event.target.value)}
                                                    inputProps={{ min: 0, max: maximum, step: 1, 'aria-label': `${label} — ${t('rewardThresholds.minimumScore')}` }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <StyledUnitAdornment position="end" $wide={!percentage}>
                                                                <StyledAdornmentUnitLabel>{unitLabel}</StyledAdornmentUnitLabel>
                                                            </StyledUnitAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </StyledTable>
                    </StyledTableContainer>

                    {validNumbers && !(validGoldOrder && validSilverOrder) && (
                        <StyledOrderAlert severity="error">
                            {!validGoldOrder && <div>{t('rewardThresholds.invalidGoldOrder')}</div>}
                            {!validSilverOrder && <div>{t('rewardThresholds.invalidSilverOrder')}</div>}
                        </StyledOrderAlert>
                    )}

                    <StyledActions>
                        <StyledSubmitButton type="submit" variant="contained" disabled={!valid || updatingRewardThresholds}>
                            <StyledTightCellText>
                                {t(updatingRewardThresholds ? 'rewardThresholds.saving' : 'rewardThresholds.save')}
                            </StyledTightCellText>
                        </StyledSubmitButton>
                    </StyledActions>
                </form>
            )}
        </StyledSection>
    );
};
